/**
 * WCC360 v2 — career copy library. PRESENTATION ONLY.
 *
 * The careers bundle stores 20 signal fields as enums (`Body_Fluids`,
 * `Intense_Irregular`, `Context_Switching`). This file is the ONLY place those
 * enums become English. Nothing here scores anything.
 *
 * Two exports matter:
 *   · FIELD_COPY      — field → enum → one specific, concrete sentence.
 *   · deriveDayShape  — the same 20 signals → a per-career day shape
 *                       (week split + hour-by-hour blocks + best/hardest part).
 *
 * Both are deterministic: the same inputs always produce the same output, which
 * is what lets the derivation script write them into the content layer and the
 * UI render them without a round trip.
 *
 * Deliberately dependency-free (no `@/` aliases) so `scripts/derive-career-content.mjs`
 * can import it directly.
 */
import type { DayInLife, DayBlock } from "./dayInLife";

export type FieldName =
  | "Context_IndoorOutdoor" | "Context_MovementLevel" | "Context_PublicSpeakingDemand"
  | "Context_PeopleContactLevel" | "Context_TravelIntensity" | "Context_ScreenTime"
  | "Context_SchedulePattern" | "Context_SensoryExposure" | "Context_ClosePhysicalWork"
  | "Value_Autonomy" | "Value_ImpactVisibility" | "Value_IncomeUpside" | "Value_Prestige"
  | "Value_Stability" | "Value_Creativity"
  | "Energy_SocialLoad" | "Energy_RecoveryDifficulty" | "Energy_CognitiveMode"
  | "Energy_CollaborationStyle" | "GruntWork_Intensity";

export type Signals = Partial<Record<FieldName, string>> & Record<string, string | null | undefined>;

/** Short human label for each field. */
export const FIELD_LABEL: Record<FieldName, string> = {
  Context_IndoorOutdoor: "Where you'd be",
  Context_MovementLevel: "How much you'd move",
  Context_ScreenTime: "Screen time",
  Context_PeopleContactLevel: "People contact",
  Context_TravelIntensity: "Travel",
  Context_SchedulePattern: "Schedule shape",
  Context_SensoryExposure: "What you'd be exposed to",
  Context_PublicSpeakingDemand: "Speaking in front of people",
  Context_ClosePhysicalWork: "Close physical work",
  Energy_SocialLoad: "Social load",
  Energy_RecoveryDifficulty: "Emotional load",
  Energy_CognitiveMode: "How you'd think",
  Energy_CollaborationStyle: "Alone or together",
  GruntWork_Intensity: "The grind",
  Value_Autonomy: "Autonomy",
  Value_ImpactVisibility: "Seeing your impact",
  Value_IncomeUpside: "Income ceiling",
  Value_Prestige: "Recognition",
  Value_Stability: "Stability",
  Value_Creativity: "Creative room",
};

/* ------------------------------------------------------------------ */
/* enum → sentence. Honest about the unpleasant parts.                 */
/* ------------------------------------------------------------------ */

export const FIELD_COPY: Record<FieldName, Record<string, string>> = {
  Context_IndoorOutdoor: {
    Mostly_Indoor: "You'd work indoors, nearly always. Weather stops being part of your day.",
    Mixed: "Some of this happens indoors and some of it doesn't. You'd be in and out through the week.",
    Mostly_Outdoor: "You'd spend most of the day outside — sun, rain and dust included.",
  },
  Context_MovementLevel: {
    Mostly_Sitting: "This is a seated job. You'd be at a desk for most of the hours you work.",
    Mixed: "You'd sit for stretches and be up on your feet for others.",
    On_the_Move: "You'd be on your feet and moving all day. It's a physical rhythm, not a desk one.",
  },
  Context_ScreenTime: {
    Low: "Very little of this happens on a screen. The work is in front of you, not on a monitor.",
    Medium: "You'd use a screen for a good part of the day, but not all of it.",
    High: "Most of the day is spent looking at a screen. That's the job, not a phase of it.",
  },
  Context_PeopleContactLevel: {
    Low: "You can do this work largely on your own. Quiet days are normal here.",
    Medium: "You'd deal with people regularly, in short bursts rather than all day.",
    High: "You'd be with people almost constantly. Talking is the work.",
    High_Distress:
      "You'd be with people on their worst days — frightened, angry, grieving. That isn't an occasional part of this role, it's the centre of it.",
  },
  Context_TravelIntensity: {
    None: "No travel. You'd work from one place.",
    Low: "You'd travel now and then, and sleep in your own bed almost every night.",
    Occasional: "You'd travel a few times a month — day trips more often than long ones.",
    Medium: "Travel is a real part of the calendar. Expect regular time away.",
    Frequent: "You'd be away often. Living out of a bag is part of the deal here.",
  },
  Context_SchedulePattern: {
    Predictable: "Your hours would be steady enough to plan a life around.",
    Variable: "Your week shifts. Some of it you'd control, some of it you wouldn't.",
    Intense_Irregular:
      "Long, uneven hours, nights and call-outs. Plans get cancelled, and you'd need people around you who understand that.",
  },
  Context_SensoryExposure: {
    No_Special_Exposure: "Nothing unusual to see, smell or hear. An ordinary working environment.",
    Smell_Noise: "You'd work around noise and strong smells. Ear protection is normal here.",
    Hazardous_Materials:
      "You'd handle hazardous material. Safety gear and procedure aren't optional, and neither is the training.",
    Body_Fluids:
      "You'll be around blood and bodily fluids. That isn't a detail you can opt out of on a bad day.",
  },
  Context_PublicSpeakingDemand: {
    Low: "You'd rarely have to stand up and present.",
    Medium: "You'd present sometimes — a team, a client, a room you already know.",
    High: "You'd speak to rooms often, including rooms that don't already agree with you.",
  },
  Context_ClosePhysicalWork: {
    No: "You'd keep normal working distance from people.",
    Yes: "You'd work close to people's bodies — touching, lifting, examining. Some people find that easy and some never do.",
  },
  Energy_SocialLoad: {
    Low: "The social demand is light. You'd finish most days with energy left.",
    Medium: "There's a steady social demand — meetings, handovers, people wanting answers.",
    High: "You'd be switched on socially all day. Even people who love it need a quiet evening after.",
  },
  Energy_RecoveryDifficulty: {
    Low: "You'd be able to put this work down when you leave it.",
    Medium: "Some days follow you home. Most don't.",
    High: "This work is hard to leave at work. The recovery is real and you'd need to plan for it.",
  },
  Energy_CognitiveMode: {
    Deep_Focus: "Long uninterrupted stretches on one problem. Depth is rewarded here.",
    Mixed: "Some deep blocks, some scattered days. It varies with the week.",
    Context_Switching: "You'd jump between things constantly. Nothing gets a whole morning to itself.",
  },
  Energy_CollaborationStyle: {
    Solo: "Most of the work is yours alone, start to finish.",
    Mixed: "You'd work solo on some things and shoulder to shoulder on others.",
    Collaborative: "Almost nothing here gets finished by one person. You'd work in a team constantly.",
  },
  GruntWork_Intensity: {
    Low: "Little of this job is busywork. Most of your hours go on the real thing.",
    Medium: "There's a routine, administrative layer to this. It's manageable, and it never goes away.",
    High:
      "A real share of this job is repetitive or administrative. People who love the mission still find this part hard.",
  },
  Value_Autonomy: {
    Low: "Someone else sets the priorities. You'd be trusted to execute, not to decide.",
    Medium: "You'd own how the work gets done, inside goals set above you.",
    High: "You'd largely run your own work — what to do, in what order, and when it's finished.",
  },
  Value_ImpactVisibility: {
    Low: "Your contribution sits deep inside something bigger. You'd rarely meet the person it helped.",
    Medium: "You'd see the effect of your work, usually a step or two removed.",
    High: "You'd see exactly who you helped, often on the same day.",
  },
  Value_IncomeUpside: {
    Low: "Pay here is modest and fairly flat over a career.",
    Medium: "Pay grows steadily with experience, without dramatic jumps.",
    High: "The ceiling here is high for people who go all the way.",
  },
  Value_Prestige: {
    Low: "This is quiet work. Few people outside it will know what you do.",
    Medium: "The work is respected by people who understand it.",
    High: "This title carries weight, in the room and outside it.",
  },
  Value_Stability: {
    Low: "Demand here moves with the market. Good years and thin years both happen.",
    Medium: "Reasonably steady work, with the usual ups and downs.",
    High: "This work holds steady. It's the kind of role people stay in for decades.",
  },
  Value_Creativity: {
    Low: "The method is set. Doing it exactly right matters more than doing it your way.",
    Medium: "There's room to bring your own approach inside a defined method.",
    High: "You'd start from a blank page more often than from a template.",
  },
};

/** One sentence for one field, or null. Never returns an enum. */
export function fieldCopy(field: string, value: string | null | undefined): string | null {
  if (!value) return null;
  const table = FIELD_COPY[field as FieldName];
  if (!table) return null;
  return table[value] ?? null;
}

/* ------------------------------------------------------------------ */
/* Pairs — where one value changes what another one means             */
/* ------------------------------------------------------------------ */

/**
 * Extra sentences that only make sense as a combination. Deterministic and
 * order-stable, so the same career always reads the same way.
 */
export function pairCopy(s: Signals): string[] {
  const out: string[] = [];
  const io = s.Context_IndoorOutdoor;
  const sens = s.Context_SensoryExposure;
  const sched = s.Context_SchedulePattern;
  const people = s.Context_PeopleContactLevel;
  const move = s.Context_MovementLevel;
  const screen = s.Context_ScreenTime;
  const rec = s.Energy_RecoveryDifficulty;
  const grunt = s.GruntWork_Intensity;
  const travel = s.Context_TravelIntensity;
  const close = s.Context_ClosePhysicalWork;

  if (io === "Mostly_Outdoor" && sens === "Hazardous_Materials")
    out.push("Outdoors and around hazardous material: the site sets the rules, and the weather doesn't excuse you from them.");
  else if (io === "Mostly_Outdoor" && sens === "No_Special_Exposure")
    out.push("Outdoors, with nothing worse than the weather to contend with. Some people would take that trade every time.");
  else if (io === "Mostly_Outdoor" && sens === "Smell_Noise")
    out.push("Outdoors and loud. You'd get fresh air and lose quiet.");

  if (sens === "Body_Fluids" && close === "Yes")
    out.push("Hands-on with people and their bodies at the same time. This is the part of the job that decides whether people last in it.");

  if (sched === "Intense_Irregular" && rec === "High")
    out.push("Hard hours and hard days together. That combination is what burns people out, so the recovery habits matter as much as the skill.");
  else if (sched === "Predictable" && rec === "Low")
    out.push("Steady hours, and work you can leave behind. If a calm life outside the job matters to you, that's a real feature here.");

  if (people === "High_Distress" && rec !== "High")
    out.push("You'd meet people in distress without carrying every case home. That's a learned skill, not a personality trait.");

  if (move === "Mostly_Sitting" && screen === "High")
    out.push("Seated and on a screen for most of the day. Your eyes, back and evenings all feel that.");
  if (move === "On_the_Move" && screen === "Low")
    out.push("Almost none of this happens at a desk. If sitting still is the part you dread, this shape suits you.");

  if (grunt === "High" && s.Value_ImpactVisibility === "High")
    out.push("The paperwork is heavy and the payoff is visible. Most people here decide the second part is worth the first.");

  if (travel === "Frequent" && sched === "Intense_Irregular")
    out.push("Frequent travel on an uneven schedule. Anything at home that needs you on a fixed day will need a plan.");

  return out;
}

/* ------------------------------------------------------------------ */
/* Derived day shape — replaces the hardcoded personas                */
/* ------------------------------------------------------------------ */

const num = (v: string | null | undefined, table: Record<string, number>, fallback: number) =>
  (v && table[v] !== undefined ? table[v] : fallback);

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

export interface WeekShape {
  deepWork: number;
  meetings: number;
  learning: number;
  admin: number;
}

/** Week split, derived from cognitive mode, collaboration, people contact and grind. */
export function deriveWeekShape(s: Signals): WeekShape {
  let deepWork = num(s.Energy_CognitiveMode, { Deep_Focus: 55, Mixed: 40, Context_Switching: 25 }, 40);
  let meetings = num(s.Energy_CollaborationStyle, { Collaborative: 34, Mixed: 22, Solo: 10 }, 22);
  meetings += num(s.Context_PeopleContactLevel, { High: 10, High_Distress: 12, Medium: 4, Low: 0 }, 4);
  let admin = num(s.GruntWork_Intensity, { High: 22, Medium: 13, Low: 6 }, 13);

  deepWork = clamp(deepWork, 15, 60);
  meetings = clamp(meetings, 8, 50);
  admin = clamp(admin, 5, 25);
  let learning = clamp(100 - deepWork - meetings - admin, 5, 25);

  // Normalise to exactly 100 without floats on screen.
  const total = deepWork + meetings + admin + learning;
  const drift = 100 - total;
  deepWork = clamp(deepWork + drift, 10, 65);
  const fixed = deepWork + meetings + admin + learning;
  if (fixed !== 100) learning = clamp(learning + (100 - fixed), 2, 30);

  return { deepWork, meetings, learning, admin };
}

const START_TIMES: Record<string, string[]> = {
  Predictable: ["9:00 AM", "10:00 AM", "1:00 PM", "3:00 PM", "5:30 PM"],
  Variable: ["8:30 AM", "10:00 AM", "12:30 PM", "3:00 PM", "6:00 PM"],
  Intense_Irregular: ["7:30 AM", "9:30 AM", "1:00 PM", "4:00 PM", "8:00 PM"],
};

/** Five blocks, chosen — never invented — from the signals. */
export function deriveDayBlocks(s: Signals): DayBlock[] {
  const times = START_TIMES[s.Context_SchedulePattern ?? "Predictable"] ?? START_TIMES.Predictable;
  const outdoor = s.Context_IndoorOutdoor === "Mostly_Outdoor";
  const mixedPlace = s.Context_IndoorOutdoor === "Mixed";
  const moving = s.Context_MovementLevel === "On_the_Move";
  const screenHigh = s.Context_ScreenTime === "High";
  const deep = s.Energy_CognitiveMode === "Deep_Focus";
  const switching = s.Energy_CognitiveMode === "Context_Switching";
  const collab = s.Energy_CollaborationStyle === "Collaborative";
  const solo = s.Energy_CollaborationStyle === "Solo";
  const peopleHeavy = s.Context_PeopleContactLevel === "High" || s.Context_PeopleContactLevel === "High_Distress";
  const distress = s.Context_PeopleContactLevel === "High_Distress";
  const gruntHigh = s.GruntWork_Intensity === "High";
  const speaks = s.Context_PublicSpeakingDemand === "High";
  const hardRecovery = s.Energy_RecoveryDifficulty === "High";

  const blocks: DayBlock[] = [];

  // 1 · Getting going
  blocks.push({
    time: times[0],
    period: "morning",
    title: outdoor ? "On site, sorting the day" : moving ? "Walk the floor, pick up the day" : "Line up the day",
    detail: outdoor
      ? "Kit checked, site checked, the order of the day agreed before anything starts moving."
      : collab
        ? "A short sync with the people you depend on, then the list gets cut to what actually matters."
        : "You decide the order of the day yourself before anyone else needs anything from you.",
    energy: 3,
  });

  // 2 · The core of the work
  blocks.push({
    time: times[1],
    period: "morning",
    title: deep ? "The long block" : switching ? "Whatever comes in" : "Core work",
    detail: deep
      ? "One problem, uninterrupted, for as long as you can hold it. This is where the value is made."
      : switching
        ? "Requests, handovers and small fires, handled in the order they arrive. Nothing gets the whole morning."
        : "Steady progress on the main task, broken up by the things only you can answer.",
    energy: deep ? 5 : 4,
  });

  // 3 · The people part
  blocks.push({
    time: times[2],
    period: "midday",
    title: distress
      ? "The hardest conversations of the day"
      : peopleHeavy
        ? "Back-to-back with people"
        : speaks
          ? "Presenting the work"
          : solo
            ? "Heads down, alone"
            : "Working it through with someone",
    detail: distress
      ? "People at their most frightened or angry. You stay steady, because that's the job."
      : peopleHeavy
        ? "One after another. Listening properly the fifth time is the skill nobody advertises."
        : speaks
          ? "You explain the work to a room, take the hard question, and hold the line where it matters."
          : solo
            ? "No meetings, no audience. Just the work and the standard you hold it to."
            : "Two heads on one problem — quicker, and usually better than either alone.",
    energy: distress ? 5 : peopleHeavy ? 4 : 4,
  });

  // 4 · The unglamorous middle
  blocks.push({
    time: times[3],
    period: "afternoon",
    title: gruntHigh ? "Records, forms and follow-ups" : screenHigh ? "Checks, review and cleanup" : "Finish and check",
    detail: gruntHigh
      ? "The administrative layer. Nobody chose this career for it, and it still has to be right."
      : screenHigh
        ? "Review what you made, fix what you missed, log what the next person needs."
        : "Close out the tasks you opened this morning and leave the place ready for tomorrow.",
    energy: gruntHigh ? 2 : 3,
  });

  // 5 · How it ends
  blocks.push({
    time: times[4],
    period: "evening",
    title: hardRecovery ? "Winding down, deliberately" : mixedPlace ? "Wrap up, plan tomorrow" : "Learn one thing",
    detail: hardRecovery
      ? "The work doesn't switch off by itself. People who last here build the walk, the call, the ritual that ends the day."
      : "Half an hour on getting better at this — reading, practising, or writing down what today taught you.",
    energy: hardRecovery ? 2 : 3,
  });

  return blocks;
}

function deriveVibe(s: Signals): string[] {
  const v: string[] = [];
  if (s.Energy_CognitiveMode === "Deep_Focus") v.push("deep focus");
  if (s.Energy_CognitiveMode === "Context_Switching") v.push("fast switching");
  if (s.Energy_CollaborationStyle === "Collaborative") v.push("team-heavy");
  if (s.Energy_CollaborationStyle === "Solo") v.push("independent");
  if (s.Context_IndoorOutdoor === "Mostly_Outdoor") v.push("outdoors");
  if (s.Context_MovementLevel === "On_the_Move") v.push("on your feet");
  if (s.Context_PeopleContactLevel === "High_Distress") v.push("high stakes");
  if (s.Value_Creativity === "High") v.push("open-ended");
  if (s.Context_SchedulePattern === "Predictable") v.push("steady hours");
  if (s.GruntWork_Intensity === "High") v.push("paperwork included");
  return v.slice(0, 4);
}

function deriveTagline(s: Signals, title: string): string {
  if (s.Context_PeopleContactLevel === "High_Distress")
    return `Being the steady one when someone else can't be. That's ${title} on most days.`;
  if (s.Value_ImpactVisibility === "High" && s.Energy_CollaborationStyle === "Collaborative")
    return "Work you can point at, done with people you'd notice if they weren't there.";
  if (s.Energy_CognitiveMode === "Deep_Focus" && s.Value_Creativity === "High")
    return "Long quiet stretches, and a blank page more often than a template.";
  if (s.Context_IndoorOutdoor === "Mostly_Outdoor")
    return "Outside, hands on the real thing, with the weather as a colleague.";
  if (s.Energy_CognitiveMode === "Context_Switching")
    return "Many small decisions a day, and the calm to keep making them.";
  return "Steady, useful work with a shape you can actually plan around.";
}

function deriveBestPart(s: Signals): string {
  if (s.Value_ImpactVisibility === "High")
    return "Seeing exactly who your work helped, usually before you go home.";
  if (s.Value_Creativity === "High")
    return "Making something that didn't exist this morning, your way.";
  if (s.Value_Autonomy === "High")
    return "Nobody standing over you. The call is yours, and so is the result.";
  if (s.Energy_CollaborationStyle === "Collaborative")
    return "A team that clicks, on a day where everyone's part lands.";
  if (s.Value_Stability === "High")
    return "Knowing this work will still be needed in twenty years, and that you'll be good at it.";
  return "The quiet satisfaction of work done properly, whether or not anyone notices.";
}

function deriveHardestPart(s: Signals): string {
  if (s.Energy_RecoveryDifficulty === "High")
    return "The days that follow you home, and learning what to do with them.";
  if (s.GruntWork_Intensity === "High")
    return "The share of the week that's forms and repetition, which never quite goes away.";
  if (s.Context_SchedulePattern === "Intense_Irregular")
    return "Cancelling something you cared about because the work needed you instead.";
  if (s.Context_SensoryExposure === "Body_Fluids" || s.Context_SensoryExposure === "Hazardous_Materials")
    return "The physical reality of the environment, on the days you're already tired.";
  if (s.Value_Autonomy === "Low")
    return "Doing it someone else's way when you can see a better one.";
  return "The stretch between what you can see should happen and what you're able to change today.";
}

/**
 * A per-career day shape, derived from the signals. No invented personas, no
 * invented tools, no named practitioner — the honest version of "a day in the
 * life" for a role nobody has interviewed yet.
 */
export function deriveDayShape(
  s: Signals,
  opts: { title: string; family?: string | null },
): DayInLife {
  return {
    slug: `derived:${opts.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    matchTitles: [],
    family: opts.family ?? undefined,
    derived: true,
    tagline: deriveTagline(s, opts.title),
    vibe: deriveVibe(s),
    weekShape: deriveWeekShape(s),
    blocks: deriveDayBlocks(s),
    bestPart: deriveBestPart(s),
    hardestPart: deriveHardestPart(s),
    tools: [],
  };
}
